import * as React from 'react';
import idx from 'idx';
import { Query } from 'react-apollo';

import { Location } from 'lib/ReactRouterTypes';
import { NavItem, GroupedNavItem } from 'components/NavItem';
import ADMIN_SETTINGS_LAYOUT_QUERY from 'admin/queries/AdminSettingsLayoutQuery.gql';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import View from 'components/View';

import { AdminSettingsLayout as AdminSettingsLayoutQueryType } from 'admin/queries/__generated__/AdminSettingsLayout';

import './styles/AdminSettingsLayout.scss';

type Props = {
  children: React.ReactElement;
  orgId: number;
  location: Location;
  params: {
    groupCode: string;
  };
};

export default function AdminSettingsLayout(props: Props) {
  const currentUser = CurrentUser.getUser();

  return (
    <Query<AdminSettingsLayoutQueryType>
      query={ADMIN_SETTINGS_LAYOUT_QUERY}
      variables={{ organizationId: props.orgId }}>
      {({ loading, error, data }) => {
        const { children, location, orgId } = props;
        if (loading) {
          return <CenteredLoadingIndicator />;
        }

        const organization = data && data.organizations && data.organizations[0];
        if (!organization || error) {
          return <ErrorReporter error={error || 'AdminSettingsLayout - could not load data'} />;
        }

        const { type, permissions } = organization;
        const adminConsolePermissions = permissions.adminConsole;
        const isDistrict = type === 'District';
        const stub = `/classes/${props.params.groupCode}/settings/organization/`;
        const path = (location.pathname.match(/\w+$/) as RegExpMatchArray)[0];

        EventTracking.view('admin_console_settings', {
          uiContext: isDistrict ? 'district' : 'school',
          component: path === 'billing' ? 'billing' : `${path}_administrators`,
        });

        const seeSettingsTabOrganizationSettings =
          currentUser?.hasFeature('org_approval_settings.web.mar-20') ||
          currentUser?.hasFeature('org_classcode_settings.web.apr-20');

        return (
          <div className="AdminSettingsLayout">
            <nav className="AdminSettingsLayout-nav">
              <ul className="AdminSettingsLayout-nav-list">
                {seeSettingsTabOrganizationSettings && (
                  <NavItem
                    data-test="organization-settings-tab"
                    to={`${stub}organization_settings`}>
                    Organization settings
                  </NavItem>
                )}
                {adminConsolePermissions.seeSettingsTabDefaultClassSettings && (
                  <NavItem
                    data-test="default-class-settings-tab"
                    to={`${stub}default_class_settings`}>
                    Default class settings
                  </NavItem>
                )}
                {adminConsolePermissions.seeSettingsTabDistrictAdmins && (
                  <NavItem data-test="district-admins-tab" to={`${stub}administrators/district`}>
                    District administrators
                  </NavItem>
                )}
                {adminConsolePermissions.seeSettingsTabSchoolAdmins && (
                  <NavItem data-test="school-admins-tab" to={`${stub}administrators/school`}>
                    School administrators
                  </NavItem>
                )}
                {adminConsolePermissions.seeSettingsTabBilling && (
                  <NavItem data-test="billing-tab" to={`${stub}billing`}>
                    Billing
                  </NavItem>
                )}
                {adminConsolePermissions.seeConnectedAccountsTab && (
                  <NavItem data-test="integrated-apps-tab" to={`${stub}integrated_tools`}>
                    App integrations
                  </NavItem>
                )}
                {adminConsolePermissions.seeSettingsTabAutoMessaging &&
                  permissions.canReceiveAutoMessages && (
                    <GroupedNavItem
                      data-test="auto-messaging-tab"
                      name="Auto Messaging"
                      items={[
                        <NavItem to={`${stub}auto_messages`}>Auto messages</NavItem>,
                        <NavItem to={`${stub}uploaded_files`}>Uploaded files</NavItem>,
                        <NavItem to={`${stub}sftp_credentials`}>SFTP credentials</NavItem>,
                      ]}
                      collapsible
                    />
                  )}
              </ul>
            </nav>
            <View className="AdminSettingsLayout-content" scroll>
              {React.cloneElement(children, {
                orgId,
                orgType: type,
                groupCode: props.params.groupCode,
                organizationUUID: organization.uuid,
                // auto messages are not necessarily associated with the viewing org,
                // so we pass along a separately named prop for clarity
                // ex) districts can create auto messages for their schools, so the
                // viewing org is the district, but the auto message org is the school
                viewingOrgUuid: organization.uuid,
              })}
            </View>
          </div>
        );
      }}
    </Query>
  );
}
